// src/services/firestoreService.ts - User data management in Firestore
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../config/firebase';

interface FirestoreUserData {
  name: string;
  userID: string;
  userType: string;
  email: string;
  phone: string;
  status: string;
  fcm_token?: string;
  firstName?: string;
  lastName?: string;
}

export class FirestoreService {
  
  /**
   * Create or update user document in Firestore
   * Similar to Flutter: fireStore.collection('users').doc(userID).set({...})
   */
  static async setUserData(
    userID: string,
    userData: Partial<FirestoreUserData>,
    merge: boolean = true
  ): Promise<void> {
    try {
      console.log("=== Firestore: Setting User Data ===");
      console.log("User ID:", userID);
      console.log("Data:", userData);

      const userRef = doc(firestore, 'users', userID);
      
      const dataToSet = {
        ...userData,
        userID: userID,
        updatedAt: serverTimestamp(),
      };


   

      await setDoc(userRef, dataToSet, { merge });
      
      console.log("✅ Firestore: User data saved successfully");
    } catch (error: any) {
      console.error("❌ Firestore: Error setting user data:", error);
      throw new Error(`Failed to save user data to Firestore: ${error.message}`);
    }
  }

  /**
   * Create initial user document for new Google user
   */
  static async createGoogleUser(
    userID: string,
    email: string,
    name: string,
    phone: string = '',
    photoURL: string = '',
    fcmToken: string = ''
  ): Promise<void> {
    try {
      const userData: FirestoreUserData = {
        name: name,
        userID: userID,
        userType: "user", // or "google_user", "premium_user" etc.
        email: email,
        phone: phone,
        status: "Unavailable",
        fcm_token: fcmToken,
    
    
      };

      await this.setUserData(userID, userData, false); // Don't merge for new users
      console.log("✅ Google user created in Firestore");
    } catch (error) {
      console.error("❌ Failed to create Google user in Firestore:", error);
      throw error;
    }
  }

  /**
   * Update user name in Firestore
   */
  static async updateUserName(
    userID: string,
    firstName: string,
    lastName: string
  ): Promise<void> {
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      
      await this.setUserData(userID, {
        name: fullName,
        firstName: firstName,
        lastName: lastName,
      });

      console.log("✅ User name updated in Firestore");
    } catch (error) {
      console.error("❌ Failed to update user name in Firestore:", error);
      throw error;
    }
  }

  /**
   * Update user phone number
   */
  static async updateUserPhone(
    userID: string,
    phone: string
  ): Promise<void> {
    try {
      await this.setUserData(userID, { phone });
      console.log("✅ User phone updated in Firestore");
    } catch (error) {
      console.error("❌ Failed to update user phone in Firestore:", error);
      throw error;
    }
  }

  /**
   * Update user status (Available/Unavailable)
   */
  static async updateUserStatus(
    userID: string,
    status: "Available" | "Unavailable"
  ): Promise<void> {
    try {
      await this.setUserData(userID, { status });
      console.log(`✅ User status updated to ${status}`);
    } catch (error) {
      console.error("❌ Failed to update user status:", error);
      throw error;
    }
  }

  /**
   * Update FCM token for push notifications
   */
  static async updateFCMToken(
    userID: string,
    fcmToken: string
  ): Promise<void> {
    try {
      await this.setUserData(userID, { fcm_token: fcmToken });
      console.log("✅ FCM token updated in Firestore");
    } catch (error) {
      console.error("❌ Failed to update FCM token:", error);
      throw error;
    }
  }

  /**
   * Get user data from Firestore
   */
  static async getUserData(userID: string): Promise<FirestoreUserData | null> {
    try {
      const userRef = doc(firestore, 'users', userID);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        console.log("✅ User data retrieved from Firestore");
        return userSnap.data() as FirestoreUserData;
      } else {
        console.log("⚠️ User document does not exist in Firestore");
        return null;
      }
    } catch (error: any) {
      console.error("❌ Failed to get user data from Firestore:", error);
      throw new Error(`Failed to retrieve user data: ${error.message}`);
    }
  }

  /**
   * Check if user exists in Firestore
   */
  static async userExists(userID: string): Promise<boolean> {
    try {
      const userRef = doc(firestore, 'users', userID);
      const userSnap = await getDoc(userRef);
      return userSnap.exists();
    } catch (error) {
      console.error("❌ Error checking user existence:", error);
      return false;
    }
  }

  /**
   * Update user type (e.g., after subscription)
   */
  static async updateUserType(
    userID: string,
    userType: string
  ): Promise<void> {
    try {
      await this.setUserData(userID, { userType });
      console.log(`✅ User type updated to ${userType}`);
    } catch (error) {
      console.error("❌ Failed to update user type:", error);
      throw error;
    }
  }

  /**
   * Sync user data from backend to Firestore
   */
  static async syncUserDataFromBackend(
    userID: string,
    backendUserData: any
  ): Promise<void> {
    try {
      const firestoreData: Partial<FirestoreUserData> = {
        name: `${backendUserData.user_fname || ''} ${backendUserData.user_lname || ''}`.trim(),
        firstName: backendUserData.user_fname || '',
        lastName: backendUserData.user_lname || '',
        email: backendUserData.email || '',
        phone: backendUserData.user_mobileno || '',
      };

      await this.setUserData(userID, firestoreData);
      console.log("✅ User data synced from backend to Firestore");
    } catch (error) {
      console.error("❌ Failed to sync user data to Firestore:", error);
      throw error;
    }
  }
}